import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { relaxationSteps } from '../../util/carSearchParams';
import { readStoredCar } from '../../util/savedCar';
import { fitSearchCar, queryFitResults, stableSort } from './carFitSearch';
import { queryWithinRadius, radiusBounds, withinRadiusOf } from './radiusSearch';
import { parseOriginParam, parseRadiusKm } from '../../util/buyerArea';
import { tireSizeToParams, rimSizeToParams } from '../../util/dimensionRoutes';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchSavedSearches } from '../SavedSearchesPage/SavedSearchesPage.duck';
import { MARKETPLACE_FILTER } from '../../util/catalogueCore';
import listingLookupCore from '../../util/listingLookupCore';

import { getConnectedUsers, getConnectedUsersListingsCount, fetchUserListings, getSavedQueries, updateSavedQuery } from '../../util/api';


// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;
// "Endast inbyten" without a search schema for isTradein (P25009-485).
const TRADEIN_ONLY_PAGE_SIZE = 100;
const { Money } = sdkTypes;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
/* loadData has started a search but has not sent it yet — it first waits on
 * the connected-users call. The page must not start a second one then
 * (SearchPageWithGrid.requestFitSearchIfMissing, P25009-492). */
export const SEARCH_LOAD_STARTED = 'app/SearchPage/SEARCH_LOAD_STARTED';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';
export const LISTING_NUMBER_MATCH = 'app/SearchPage/LISTING_NUMBER_MATCH';

export const UPDATE_FAVOURITES_REQUEST = 'app/SearchPage/UPDATE_FAVOURITES_REQUEST';
export const UPDATE_FAVOURITES_SUCCESS = 'app/SearchPage/UPDATE_FAVOURITES_SUCCESS';
export const UPDATE_FAVOURITES_ERROR = 'app/SearchPage/UPDATE_FAVOURITES_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const LOAD_ADVERTS_SUCCESS = 'app/SearchPage/LOAD_ADVERTS_SUCCESS';

export const GET_CONNECTED_USERS_REQUEST = 'app/SearchPage/GET_CONNECTED_USERS_REQUEST';
export const GET_CONNECTED_USERS_SUCCESS = 'app/SearchPage/GET_CONNECTED_USERS_SUCCESS';
export const GET_CONNECTED_USERS_ERROR = 'app/SearchPage/GET_CONNECTED_USERS_ERROR';

export const FETCH_USER_LISTING_COUNTS_REQUEST = 'app/SearchPage/FETCH_USER_LISTING_COUNTS_REQUEST';
export const FETCH_USER_LISTING_COUNTS_SUCCESS = 'app/SearchPage/FETCH_USER_LISTING_COUNTS_SUCCESS';
export const FETCH_USER_LISTING_COUNTS_ERROR = 'app/SearchPage/FETCH_USER_LISTING_COUNTS_ERROR';

export const GET_SAVED_QUERIES_REQUEST = 'app/SearchPage/GET_SAVED_QUERIES_REQUEST';
export const GET_SAVED_QUERIES_SUCCESS = 'app/SearchPage/GET_SAVED_QUERIES_SUCCESS';
export const GET_SAVED_QUERIES_ERROR = 'app/SearchPage/GET_SAVED_QUERIES_ERROR';


export const SET_EDITING_QUERY_ID = 'app/SearchPage/SET_EDITING_QUERY_ID';

/* P25009-432 BR-8 — after a 0-result search, one count query per removable
 * filter prices out the empty state's one-tap widenings ("Visa träffar i
 * hela Sverige — N"). */
export const WIDENING_COUNTS_SUCCESS = 'app/SearchPage/WIDENING_COUNTS_SUCCESS';


export const UPDATE_SAVED_QUERY_REQUEST = 'app/SearchPage/UPDATE_SAVED_QUERY_REQUEST';
export const UPDATE_SAVED_QUERY_SUCCESS = 'app/SearchPage/UPDATE_SAVED_QUERY_SUCCESS';
export const UPDATE_SAVED_QUERY_ERROR = 'app/SearchPage/UPDATE_SAVED_QUERY_ERROR';

// ================ Reducer ================ //

const initialState = {
  updateFavouritesInProgress: false,
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  /* Counts searches started; only the newest may write its results. In the
   * store, not in the module, so concurrent server renders cannot see each
   * other's searches. */
  searchSeq: 0,
  currentPageResultIds: [],
  currentPageAdverttsIds: [],
  getConnectedUsersinProgress: false,
  getConnectedUsersError: null,
  connectedUsers: [],
  userListingCounts: {},
  getConnectedUsersInProgress: false,
  fetchUserListingCountsError: null,
  getSavedQueriesInProgress: false,
  getSavedQueriesError: null,
  savedQueries: [],
  editingQueryId: null,
  updateSavedQueryInProgress: false,
  updateSavedQueryError: null,
  relaxedFilters: [],
  /* The car search's fit result (P25009-492): { applied, level, checked,
   * truncated } when the results were checked for fit before paging, null
   * for an ordinary search. */
  fitResult: null,
  searchLoadPending: false,
  /* param key → totalItems when that one filter is dropped; filled only
   * after a 0-result search (BR-8). */
  wideningCounts: {},
  /* P25009-512 BR-7 — the search text was a listing number with public
   * matches: { tireId, listingIds, redirectListingId }. redirectListingId is
   * set when exactly one listing has it — the page then opens that listing. */
  listingNumberMatch: null,
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchSeq: (state.searchSeq || 0) + 1,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
        currentPageAdverttsIds: [],
        relaxedFilters: [],
        fitResult: null,
        wideningCounts: {},
        listingNumberMatch: null,
      };
    case LISTING_NUMBER_MATCH:
      return { ...state, listingNumberMatch: payload };
    case SEARCH_LOAD_STARTED:
      return { ...state, searchLoadPending: true };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        searchLoadPending: false,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
        relaxedFilters: payload.relaxedFilters || [],
        fitResult: payload.fit || null,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        searchInProgress: false,
        searchLoadPending: false,
        searchListingsError: payload,
      };

    case WIDENING_COUNTS_SUCCESS:
      return { ...state, wideningCounts: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    case UPDATE_FAVOURITES_REQUEST:
      return {
        ...state,
        updateFavouritesInProgress: true
      }
    case UPDATE_FAVOURITES_SUCCESS:
    case UPDATE_FAVOURITES_ERROR:
      return {
        ...state,
        updateFavouritesInProgress: false
      }

    case LOAD_ADVERTS_SUCCESS:
      return {
        ...state,
        currentPageAdverttsIds: resultIds(payload.data),
      };
    case GET_CONNECTED_USERS_REQUEST:
      return {
        ...state,
        getConnectedUsersinProgress: true,
        searchListingsError: null,
      };
    case GET_CONNECTED_USERS_SUCCESS:
      return {
        ...state,
        getConnectedUsersinProgress: false,
        searchListingsError: null,
        connectedUsers: payload,
      };
    case GET_CONNECTED_USERS_ERROR:
      return {
        ...state,
        getConnectedUsersinProgress: false,
        searchListingsError: payload,

      };

    case FETCH_USER_LISTING_COUNTS_REQUEST:
      return {
        ...state,
        getConnectedUsersInProgress: true,
        fetchUserListingCountsError: null,
      };

    case FETCH_USER_LISTING_COUNTS_SUCCESS:
      return {
        ...state,
        getConnectedUsersInProgress: false,
        userListingCounts: payload,
      };

    case FETCH_USER_LISTING_COUNTS_ERROR:
      return {
        ...state,
        getConnectedUsersInProgress: false,
        fetchUserListingCountsError: payload,
      };

    case GET_SAVED_QUERIES_REQUEST:
      return {
        ...state,
        getSavedQueriesInProgress: true,
        getSavedQueriesError: null,
      };

    case GET_SAVED_QUERIES_SUCCESS:
      return {
        ...state,
        getSavedQueriesInProgress: false,
        savedQueries: payload,
      };

    case GET_SAVED_QUERIES_ERROR:
      return {
        ...state,
        getSavedQueriesInProgress: false,
        getSavedQueriesError: payload,
      };

    case SET_EDITING_QUERY_ID:
      return {
        ...state,
        editingQueryId: action.payload,
      };

    case UPDATE_SAVED_QUERY_REQUEST:
      return {
        ...state,
        updateSavedQueryInProgress: true,
        updateSavedQueryError: null,
      };

    case UPDATE_SAVED_QUERY_SUCCESS:
      return {
        ...state,
        updateSavedQueryInProgress: false,
        editingQueryId: null,
      };

    case UPDATE_SAVED_QUERY_ERROR:
      return {
        ...state,
        updateSavedQueryInProgress: false,
        updateSavedQueryError: payload,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

/* `relaxedFilters` lists the car-search params that had to be dropped for this
 * result to be non-empty. The page shows them so a widened search never looks
 * like a plain one (BR-5). `fit` is the car search's fit result
 * (carFitSearch.js), null for an ordinary search. */
export const searchListingsSuccess = (response, relaxedFilters = [], fit = null) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, relaxedFilters, fit },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const listingNumberMatch = match => ({
  type: LISTING_NUMBER_MATCH,
  payload: match,
});

/* Two sellers can share a listing number (P25009-512 Out of scope A), so a
 * lookup asks for a handful and shows them all (BR-10). */
const LISTING_NUMBER_LOOKUP_SIZE = 10;

/* The params that shape how a listing is returned — never which ones match. */
const LISTING_NUMBER_DISPLAY_PARAMS = /^(include|fields\.|limit\.|imageVariant\.)/;

/**
 * P25009-512 BR-7 — the public search text is a listing number.
 *
 * Asks for the published listings that carry it. Hidden ads (trade-ins, a
 * seller's hidden stock) and soft-deleted ones are never a public match
 * (C-14), and every result is checked against its own metadata, because the
 * API ignores a meta_* filter it has no search schema for and would answer
 * with arbitrary listings.
 *
 *   1 match    → the results are that listing, and the page opens it
 *   2 or more  → the results are exactly those listings (with seller names)
 *   0 / error  → null: the caller runs the normal keyword search
 *
 * The reg plate is NEVER looked up here (BR-8) — only normaliseTireId is used.
 */
export const searchByListingNumber = (tireId, searchParams, config) => async (
  dispatch,
  getState,
  sdk
) => {
  const params = {
    ...Object.fromEntries(
      Object.entries(searchParams || {}).filter(([key]) => LISTING_NUMBER_DISPLAY_PARAMS.test(key))
    ),
    ...(searchParams?.pub_listingType ? { pub_listingType: searchParams.pub_listingType } : {}),
    meta_tireId: tireId,
    pub_isDeleted: false,
    perPage: LISTING_NUMBER_LOOKUP_SIZE,
  };
  try {
    const response = await sdk.listings.query(params);
    const matches = (response?.data?.data || []).filter(listing => {
      const { metadata = {}, publicData = {} } = listing?.attributes || {};
      return (
        String(metadata.tireId || '')
          .trim()
          .toUpperCase() === tireId &&
        publicData.isHide !== true &&
        publicData.isDeleted !== true
      );
    });
    if (matches.length === 0) return null;

    const narrowed = {
      ...response,
      data: {
        ...response.data,
        data: matches,
        meta: { ...response.data.meta, totalItems: matches.length, totalPages: 1, page: 1 },
      },
    };
    dispatch(searchListingsRequest(params));
    dispatch(addMarketplaceEntities(narrowed, { listingFields: config?.listing?.listingFields }));
    dispatch(
      listingNumberMatch({
        tireId,
        listingIds: matches.map(listing => listing.id.uuid),
        redirectListingId: matches.length === 1 ? matches[0].id.uuid : null,
      })
    );
    dispatch(searchListingsSuccess(narrowed));
    return narrowed;
  } catch (e) {
    // A marketplace without the meta_tireId schema answers 400 — search as text.
    return null;
  }
};

export const loadAdvertssSuccess = response => ({
  type: LOAD_ADVERTS_SUCCESS,
  payload: { data: response.data },
});

export const getConnectedUsersRequest = () => ({
  type: GET_CONNECTED_USERS_REQUEST,
});

export const getConnectedUserssuccess = (response) => ({
  type: GET_CONNECTED_USERS_SUCCESS,
  payload: response,
});

export const getConnectedUsersError = (e) => ({
  type: GET_CONNECTED_USERS_ERROR,
  error: true,
  payload: e,
});

export const fetchUserListingCountsRequest = () => ({ type: FETCH_USER_LISTING_COUNTS_REQUEST });
export const fetchUserListingCountsSuccess = counts => ({ type: FETCH_USER_LISTING_COUNTS_SUCCESS, payload: counts });
export const fetchUserListingCountsError = e => ({ type: FETCH_USER_LISTING_COUNTS_ERROR, error: true, payload: e });

export const getSavedQueriesRequest = () => ({ type: GET_SAVED_QUERIES_REQUEST });
export const getSavedQueriesSuccess = savedQueries => ({ type: GET_SAVED_QUERIES_SUCCESS, payload: savedQueries });
export const getSavedQueriesError = e => ({ type: GET_SAVED_QUERIES_ERROR, error: true, payload: e });

export const setEditingQueryId = id => ({ type: SET_EDITING_QUERY_ID, payload: id })

export const updateSavedQueryRequest = () => ({ type: UPDATE_SAVED_QUERY_REQUEST });
export const updateSavedQuerySuccess = () => ({ type: UPDATE_SAVED_QUERY_SUCCESS });
export const updateSavedQueryError = e => ({ type: UPDATE_SAVED_QUERY_ERROR, error: true, payload: e });

export const updateUserSavedQuery = (payload) => (dispatch, getState, sdk) => {
  dispatch(updateSavedQueryRequest());
  return updateSavedQuery({ id: payload.id, params: payload.params })
    .then(() => {
      dispatch(updateSavedQuerySuccess())
      return null
    })
    .catch((e) => dispatch(updateSavedQueryError(e)));
}

export const fetchConnectedUsers = (params) => (dispatch, getState, sdk) => {
  dispatch(getConnectedUsersRequest());
  return getConnectedUsers({})
    .then((res) => {
      dispatch(getConnectedUserssuccess(res.data));
      return res.data;
    })
    .catch((e) => dispatch(getConnectedUsersError(e)));
}

export const fetchUserListingCounts = (userIds, filters) => (dispatch, getState, sdk) => {
  dispatch(fetchUserListingCountsRequest());

  const cleanedFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, v]) => v != null && v !== '')
  );

  return getConnectedUsersListingsCount({ userIds, filters: cleanedFilters })
    .then(res => {
      dispatch(fetchUserListingCountsSuccess(res.data));
    })
    .catch(e => {
      dispatch(fetchUserListingCountsError(e));
    });
};

export const getUserSavedQueries = (params) => (dispatch, getState, sdk) => {
  dispatch(getSavedQueriesRequest());
  return getSavedQueries({})
    .then((res) => {
      // console.log('res',res)
      dispatch(getSavedQueriesSuccess(res.savedQueries));
      return res.savedQueries;
    })
    .catch((e) => dispatch(getSavedQueriesError(e)));
}

/* Params that are plumbing rather than user-set filters — never offered as
 * something to widen away. */
const WIDENING_EXCLUDED_KEYS = [
  'pub_listingType',
  'pub_isReserved',
  'pub_isHide',
  'pub_isDeleted',
  'pub_isTradein',
  // P25009-410 — widening must never offer the seller's catalogue as results.
  'pub_visibility',
];

/* P25009-432 BR-8 — the empty state offers one-tap widening chips. Price
 * each of them with a 1-item count query (same query minus that one filter)
 * so a chip can say "Visa träffar i hela Sverige — 132" and a chip that
 * would still return nothing is not offered at all (BR-6). Runs only after
 * a search that ended on 0 results, so the extra queries are rare. */
export const fetchWideningCounts = (params, { radius = false } = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  /* A distance chip (P25009-425) is widened by dropping its box: the chip in
   * the URL is `radius`, so that is the key its count is filed under. */
  const radiusKeys = radius && params.bounds ? ['bounds'] : [];
  const candidateKeys = [
    ...radiusKeys,
    ...Object.keys(params)
      .filter(
        k => (k.startsWith('pub_') && !WIDENING_EXCLUDED_KEYS.includes(k)) || k === 'price'
      )
      .slice(0, 8),
  ];
  if (candidateKeys.length === 0) return;

  try {
    const entries = await Promise.all(
      candidateKeys.map(async key => {
        const wider = { ...params, page: 1, perPage: 1 };
        delete wider[key];
        /* A model without its make is not a valid category query. */
        if (key === 'pub_categoryLevel1') delete wider.pub_categoryLevel2;
        const res = await sdk.listings.query(wider);
        return [key === 'bounds' ? 'radius' : key, res.data.meta.totalItems];
      })
    );
    dispatch({ type: WIDENING_COUNTS_SUCCESS, payload: Object.fromEntries(entries) });
  } catch (e) {
    /* Counts are an enhancement — the plain widening chips work without
     * them, so a failed count query changes nothing. */
  }
};

/* `fitCar`: the plate payload when this is the car's own fit search
 * (carFitSearch.fitSearchCar) — the results are then checked for fit before
 * they are paged. `radius`: { center, km } of a distance chip (P25009-425) —
 * the results are then trimmed from the API's box to the circle before they
 * are paged (radiusSearch.js). */
export const searchListings = (
  searchParams, config, showOutOfStock, showInternalStock, isAdvert, fitCar = null, radius = null,
) => (dispatch, getState, sdk) => {
  if (!isAdvert) {
    dispatch(searchListingsRequest(searchParams));
  };
  /* Only the newest search may write the result list. Every URL change starts
   * a search, and two can be in flight at once — opening /annonser with a
   * remembered car runs the plain search, then RememberedCarSearch turns the
   * URL into the car search a moment later. Whichever answered LAST used to
   * win, so the big unfiltered query regularly overwrote the car's results
   * (P25009-482). An older search's answer — or error — is now dropped. */
  const searchSeq = isAdvert ? null : getState().SearchPage?.searchSeq;
  const isSuperseded = () =>
    searchSeq != null && getState().SearchPage?.searchSeq !== searchSeq;

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
        pub_listingType: listingTypes.map(l => l.listingType),
        // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
        // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
      }
      : {};
  };

  const omitInvalidCategoryParams = params => {


    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue = typeof params?.[levelKey] !== 'undefined'
        ? `${params?.[levelKey]}`
        : undefined;

      const foundCategory =
        levelValue && categories.find(cat => cat.id.toLowerCase() === levelValue.toLowerCase());

      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
          [levelKey]: foundCategory.id,
          ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
        }
        : foundCategory
          ? { [levelKey]: foundCategory.id }
          : {};
    };


    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );
    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
        price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
      }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
          ? getExclusiveEndDate(endRaw, searchTZ)
          : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
        start: getProlongedStart(startDate),
        end: getProlongedEnd(endDate),
        // Availability can be time-full or time-partial.
        // However, due to prolonged time window, we need to use time-partial.
        availability: 'time-partial',
        // minDuration uses minutes
        minDuration,
      }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse || showOutOfStock ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const { perPage, price, dates, sort, mapSearch, ...restOfParams } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  /* The Marketplace API rejects `sort` + `origin` together ("'sort' can not
   * be used in combination with 'origin'"). When the search has an origin,
   * distance IS the ranking, so no sort is sent — not even the boost default.
   * Before P25009-445 this collision could never happen because origin was
   * always stripped before the call (see loadData).
   *
   * stableSort ends every sort on createdAt: most listings share their boost
   * date and a cron rewrites pub_updateDate weekly, and on such ties the API's
   * order is not stable between pages — page 2 repeated listings from page 1
   * and skipped others (P25009-492). */
  const sortMaybe =
    restOfParams.origin || sort === config.search.sortConfig.relevanceKey
      ? {}
      : { sort: stableSort(sort || 'meta_boostStartDate,pub_updateDate') };
  /* P25009-410 — buyers search ads only; a catalogue row is on its seller's
   * profile. Own-stock mode is the seller's whole stock, catalogue included. */
  const showReservedMaybe = showInternalStock
    ? {}
    : { pub_isReserved: false, pub_isHide: false, ...MARKETPLACE_FILTER };

  const params = {
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(restOfParams),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...sortMaybe,
    ...showReservedMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };

  /**
   * Run the query; if a car search found nothing, run it again with one fewer
   * condition, until it finds something or runs out of conditions to give up.
   *
   * A car search is precise on purpose — rim width, hub bore and an offset
   * window, as BR-6 asks. On this marketplace that precision often lands on
   * zero: rim width is filled in on 2% of rim listings, hub bore on 3%, and
   * the offset window is one-sided. The strict query still runs first, so a
   * buyer who asks for 7" rims with a 57.1 mm hub gets exactly those whenever
   * such stock exists. Only when that finds nothing does the search widen, and
   * the page then says plainly which conditions it gave up (AC-1, AC-5).
   *
   * Only params the car search wrote are eligible, they go one step at a time,
   * and the bolt pattern is never among them — see relaxationSteps() in
   * util/carSearchParams.js for the order and the reasoning.
   */
  const steps = relaxationSteps(params);
  /* A distance chip's search keeps only the circle's listings, and pages
   * those (P25009-425); every other search is one API query. */
  const runQuery = radius && !isAdvert
    ? queryParams =>
        queryWithinRadius({
          sdk,
          params: queryParams,
          radius,
          page: Number(queryParams.page) || 1,
          perPage,
        })
    : queryParams => sdk.listings.query(queryParams);
  const queryWithWidening = async () => {
    /* The car's own search: fit first, then paged (P25009-492). It widens
     * itself the same way, but on "nothing fits" rather than "nothing found". */
    if (fitCar && !isAdvert) {
      return queryFitResults({
        sdk,
        params,
        car: fitCar,
        page: Number(params.page) || 1,
        perPage,
        listingFields: config?.listing?.listingFields,
        keep: radius ? withinRadiusOf(radius) : null,
      });
    }
    const response = await runQuery(params);
    if (response.data.meta.totalItems > 0 || steps.length === 0) {
      return { response, relaxed: [] };
    }

    const relaxed = [];
    for (const step of steps) {
      relaxed.push(...step);
      const widerParams = { ...params };
      relaxed.forEach(dropped => delete widerParams[dropped]);
      const widened = await runQuery(widerParams);
      if (widened.data.meta.totalItems > 0) {
        return { response: widened, relaxed };
      }
    }

    /* Nothing fit even with everything relaxed. Report the search as it was
     * asked, with no relaxation: saying "we dropped navhål and ET and are
     * showing you these instead" above an empty page describes something that
     * did not happen, and leaves the shopper with nothing to act on. The
     * ordinary empty state, whose chips match the URL, is the honest answer. */
    return { response, relaxed: [] };
  };

  /* P25009-485 BR-4 — until `isTradein` has a search schema the API ignores
   * pub_isTradein, so "Endast inbyten" also gets hidden ads that are not
   * trade-ins. They are dropped here, and when any were, the count is what is
   * left: the mode asks for up to TRADEIN_ONLY_PAGE_SIZE listings on one page
   * (see loadData). Once the schema exists nothing is dropped and the API's
   * own count and paging stand. */
  const onlyTradeinsIfAsked = response => {
    if (params.pub_isTradein !== true) return response;
    const all = response.data.data;
    const tradeins = all.filter(l => l?.attributes?.publicData?.isTradein === true);
    if (tradeins.length === all.length) return response;
    return {
      ...response,
      data: {
        ...response.data,
        data: tradeins,
        meta: { ...response.data.meta, totalItems: tradeins.length, totalPages: 1, page: 1 },
      },
    };
  };

  return queryWithWidening()
    .then(({ response: rawResponse, relaxed, fit = null }) => {
      const response = onlyTradeinsIfAsked(rawResponse);
      // A newer search owns the page now; this answer is for a URL that is gone.
      if (isSuperseded()) return response;
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      if (isAdvert) {
        dispatch(loadAdvertssSuccess(response));
      } else {
        dispatch(searchListingsSuccess(response, relaxed, fit));
        /* BR-8 — a search that dead-ends prices out its widening chips. Not
         * after a fit search: those counts would be of listings that do not
         * fit, and the fit bar already offers "Visa även ej passande". */
        if (response.data.meta.totalItems === 0 && !fit) {
          dispatch(fetchWideningCounts(params, { radius: !!radius }));
        }
      };

      return response;
    })
    .catch(e => {
      if (!isAdvert && !isSuperseded()) {
        dispatch(searchListingsError(storableError(e)));
      };
      throw e;
    });
};
export const updateFavouritesRequest = () => ({
  type: UPDATE_FAVOURITES_REQUEST,
})

export const updateFavouritesSuccess = response => ({
  type: UPDATE_FAVOURITES_SUCCESS,
  payload: { data: response.data },
});

export const updateFavouritesError = e => ({
  type: UPDATE_FAVOURITES_ERROR,
  error: true,
  payload: e,
});

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});


export const updateFavourites = (newFavoriteListings) => (dispatch, getState, sdk) => {
  dispatch(updateFavouritesRequest());
  return sdk.currentUser.updateProfile({
    publicData: {
      favoriteListings: newFavoriteListings
    }
  })
    .then(response => {
      dispatch(updateFavouritesSuccess(response));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(updateFavouritesError(storableError(e)));
      throw e;
    });
}

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  dispatch({ type: SEARCH_LOAD_STARTED });

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { make, model, tireSize, rimSize } = params || {};
  const categoryParamsFromPath = {
    ...(make && { pub_categoryLevel1: make }),
    ...(model && { pub_categoryLevel2: model }),
  };

  // Dimension landing pages (/dack/:tireSize, /falgar/:rimSize) pre-apply the
  // matching pub_* filters from the clean URL slug.
  const dimensionParamsFromPath = {
    ...(tireSize ? tireSizeToParams(tireSize) : {}),
    ...(rimSize ? rimSizeToParams(rimSize) : {}),
  };

  // Merge URL query params + path params
  const mergedQueryParams = {
    ...queryParams,
    ...categoryParamsFromPath,
    ...dimensionParamsFromPath,
  };
  /* UI-only params, none of which the Marketplace API should ever see.
   *
   * `axle` records that the buyer deliberately narrowed a staggered set to one
   * axle, so the results can say so (P25009-364 AC-2) — the actual narrowing
   * is already in pub_combination.
   *
   * `regNumber`, `carMake` and `carModel` say which car the search is about.
   * They rank the results and label the car chip; making the make or model a
   * condition is what hid compatible stock in the first place (BR-2).
   *
   * `passar=1` is "Visa delar som passar" in the Din bil box (P25009-482): the
   * page hides the cards the fit check does not pass. The fit check needs the
   * car's data, which the API does not have, so it is applied in the page. */
  const {
    selectedUserId,
    page = 1,
    address,
    origin,
    searchType,
    stock,
    axle,
    regNumber,
    /* A car chosen by model, without a plate (P25009-491) — like regNumber. */
    carId,
    carMake,
    carModel,
    passar,
    /* "Endast inbyten" (P25009-485 BR-4) — only inside the own-stock mode. */
    inbyte,
    /* A distance chip, in km (P25009-425 BR-2). Becomes `bounds` + a circle
     * trim below; the API never sees it. */
    radius,
    ...rest
  } = mergedQueryParams;
  /* P25009-445 "Närmast dig": pass `origin` to the API whenever it ranks the
   * results. The old gate (`isOriginInUse`) required mainSearch.searchType to
   * be 'location' AND maps.search.sortSearchByDistance — but the hosted
   * Console asset pins searchType to 'keywords', so the gate was permanently
   * false and the nearMe sort prompted for a position only to rank by nothing
   * (P25009-341).
   *
   * P25009-425: `origin` is the buyer's area. It ranks by distance only when
   * no other sort is chosen ("Närmast mig" = an origin and no sort) — the API
   * takes `sort` or `origin`, never both, and never origin with keywords.
   * Next to another sort it is still the centre of a distance chip and what
   * the cards measure from; it is just not sent. */
  const ranksByDistance =
    !!origin && !rest.keywords && (!rest.sort || rest.sort === 'nearMe');
  const originMaybe = ranksByDistance ? { origin } : {};
  const radiusKm = parseRadiusKm(radius);
  const radiusCenter = radiusKm ? parseOriginParam(origin) : null;
  const radiusSearch = radiusCenter ? { center: radiusCenter, km: radiusKm } : null;
  /* The chip's box replaces a map viewport's: one area at a time. */
  const radiusBoundsMaybe = radiusSearch ? { bounds: radiusBounds(radiusSearch) } : {};

  /* P25009-445 tyre-aware sorts. 'mountedTotal' and 'fastestReady' are not
   * Marketplace API sort keys: mountedTotal fetches cheapest-first and is
   * re-ranked client-side with the seller's montering price, fastestReady
   * keeps the server ranking and is re-ranked client-side (see
   * resultCardData.js). 'bestMatch' and 'nearMe' never normally reach this
   * point (bestMatch clears the param, nearMe becomes `origin`), but an old
   * or hand-edited link must not hit the API with an invalid sort key. */
  const SYNTHETIC_SORTS = { mountedTotal: '-price', fastestReady: null, bestMatch: null, nearMe: null };
  if (Object.prototype.hasOwnProperty.call(SYNTHETIC_SORTS, rest.sort)) {
    const serverSort = SYNTHETIC_SORTS[rest.sort];
    if (serverSort) {
      rest.sort = serverSort;
    } else {
      delete rest.sort;
    }
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const userId = getState()?.user?.currentUser?.id?.uuid

  const showOutOfStock = stock == "no-stock"
  /* Own-stock mode ("Visa mitt lager", P25009-485 BR-4). The author is always
   * the signed-in account from the session — never a URL parameter — so a
   * crafted link cannot list another seller's hidden stock through this page.
   * Sub-users sign in as their company's account, so they see its stock. */
  const showInternalStock = searchType == "ownListing" && userId
  const authorIdMaybe = showInternalStock ? { authorId: userId } : {}
  /* "Endast inbyten": a trade-in is always hidden, and isHide has a search
   * schema, so the API narrows to hidden stock; pub_isTradein narrows further
   * once a search schema exists for it (none yet — see searchListings). */
  const tradeinOnlyMaybe =
    showInternalStock && String(inbyte) === '1' // parse() turns '1' into 1
      ? { pub_isTradein: true, pub_isHide: true }
      : {}

  /* The snake-case `author_id` from a URL would ride along next to the
   * session's authorId; in own-stock mode it is dropped so the session's is
   * the only author the API sees. */
  if (showInternalStock) delete rest.author_id;

  const showListingsParams = {
    ...rest,
    ...originMaybe,
    ...radiusBoundsMaybe,
    ...authorIdMaybe,
    ...tradeinOnlyMaybe,
    page,
    'pub_isDeleted': false,
    // currentStock: the cards' "12 i lager" (BR-6) reads the Sharetribe stock
    // directly — publicData.quantity is the seller-entered count and drifts.
    include: ['author', 'images', 'author.profileImage', 'currentStock'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      // P25009-486 BR-3: a fit search hides listings untouched for 12 months
      // (priceStockUpdatedAt, else createdAt — resultCardData isStaleListing).
      'createdAt',
      'publicData.priceStockUpdatedAt',
      'publicData.listingType',
      'publicData.externalLink',
      'publicData.subDescription',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      // These help rendering of 'purchase' listings,
      // when transitioning from search page to listing page
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.county',
      // The card's town is read from the stored place name, which holds it
      // after the postcode — publicData.city is often the street or a
      // postcode (P25009-425 BR-3, util/listingTown.js).
      'publicData.location',
      'publicData.isHide',
      // The seller's own "Inbyte · dold" badge in own-stock mode (P25009-485).
      'publicData.isTradein',
      'publicData.boltCircle',
      'publicData.numberOfBolts',
      'publicData.tireWidthFront',
      'publicData.profileFront',
      'publicData.diameter',
      'publicData.threaddepth',
      'publicData.condition',
      'publicData.hubHole',
      'publicData.etFront',
      'publicData.city',
      'publicData.updateDate',
      'publicData.totalLikes',
      // Needed to build the breadcrumb listing URL on the cards
      // (/l/:category/:make/:model/:slug/:id) — see util/listingSeoUrl.js.
      'publicData.adType',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      // P25009-445 result cards. The card already read quantity/brand/sasong
      // but the query never requested them, so those lines silently never
      // rendered on search results.
      'publicData.quantity',
      'publicData.brand',
      'publicData.sasong',
      'publicData.season',
      'publicData.tiretype',
      'publicData.salesUnit',
      'publicData.setSize',
      // resolveSalesUnit locks staggered pairs and packages to "set"; without
      // these the card priced them per piece (P25009-486 BR-3, price for 4).
      'publicData.tireWidthRear',
      'publicData.profileRear',
      'publicData.etRear',
      'publicData.isReachShell',
      // EU tyre label (BR-6). No listing carries this yet — the supplier
      // feed is the intended writer — but the card renders it when present.
      'publicData.euLabel',
      'metadata',
    ],
    // profile.publicData.services: the montering line + "Lägsta totalpris
    // monterat" read the seller's flat montering price off the author.
    'fields.user': ['profile.displayName', 'profile.bio', 'profile.abbreviatedName', 'profile.metadata', 'profile.publicData.userType', 'profile.publicData.services'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 3,
  };

  const normalListingsParams = {
    ...showListingsParams,
    // "Endast inbyten" is one page of a workshop's hidden stock (see searchListings).
    perPage: tradeinOnlyMaybe.pub_isTradein ? TRADEIN_ONLY_PAGE_SIZE : RESULT_PAGE_SIZE,
  };

  if (!normalListingsParams.pub_listingType) {
    const listingTypes = config.listing.listingTypes.filter(l => l.listingType !== "admin-adverts" && l.listingType !== "buying").map(l => l.listingType).join(",");
    normalListingsParams.pub_listingType = listingTypes;
  };

  // Everything in this block is for a signed-in visitor in a browser. The calls
  // go to this app's own /api routes, which authenticate from the session cookie,
  // and no cookie is sent while the server renders the page — so on the server
  // they can only ever fail. Skipping them costs nothing, because Routes.js calls
  // loadData again as soon as the page hydrates.
  //
  // The try/catch matters just as much: this used to be an unguarded await, so
  // one throw here aborted the whole loadData before the listing search below
  // ever ran. Server-side that left every /annonser, /dack and /falgar page with
  // zero listings, and zero listings renders a "noindex" tag.
  const isBrowser = typeof window !== 'undefined';
  if (isBrowser) {
    try {
      await dispatch(fetchConnectedUsers()).then(connectedUsers => {
        const currentUserId = getState()?.user?.currentUser?.id?.uuid;
        const connectedUserIds = connectedUsers?.map(user => user?.id?.uuid).filter(Boolean) || [];
        const allUserIds = currentUserId ? [currentUserId, ...connectedUserIds] : connectedUserIds;

        const shouldShowOutOfStock = stock === 'no-stock';

        const stockFiltersForCounts = shouldShowOutOfStock
          ? {}
          : {
            minStock: 1,
            stockMode: 'match-undefined',
          };

        const listingCountParams = {
          ...normalListingsParams,
          ...stockFiltersForCounts,
        };

        if (allUserIds.length > 0) {
          dispatch(fetchUserListingCounts(allUserIds, listingCountParams));
        }
        dispatch(getUserSavedQueries());
        /* Bevakningar feed the "Bevakade sökningar" rail above the results
         * (P25009-453 BR-8) — same collection the /bevakningar page shows.
         * Guests have none, and the thunk rethrows its errors, so gate and
         * swallow: a failed fetch only costs the rail, never the search. */
        if (currentUserId) {
          dispatch(fetchSavedSearches()).catch(() => {});
        }
        // dataLayer only exists once the tag manager script has loaded, so check
        // for it rather than assuming it: an early search would otherwise throw.
        if (window.dataLayer && queryParams && Object.keys(queryParams).length > 0) {
          window.dataLayer.push({
            event: 'user-search',
            pagePath: window.location.pathname,
            data: queryParams,
          });
        }
      });
    } catch (e) {
      // Each thunk above already dispatches its own error action, so there is
      // nothing to add to the store here. What must not happen is this error
      // reaching the caller and skipping the listing search below.
    }
  }




  if (selectedUserId) {
    try {
      dispatch(searchListingsRequest(normalListingsParams))
      if (stock === 'no-stock') {
        delete normalListingsParams.minStock;
        delete normalListingsParams.stockMode;
      } else {
        normalListingsParams.minStock ??= 1;
        normalListingsParams.stockMode ??= 'match-undefined';
      }
      const response = await fetchUserListings({
        filters: normalListingsParams,
        userId: selectedUserId,
      });
      // console.log('response', response.data.data);
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      response?.data?.data?.forEach(l => {
        if (l?.attributes?.price) {
          l.attributes.price = new Money(l.attributes.price.amount, l.attributes.price.currency);
        }
      });
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return dispatch(searchListingsSuccess(response));
    } catch (e) {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    }
  } else {
    // const advertListingsParams = {
    //   ...showListingsParams,
    //   pub_listingType: "admin-adverts",
    //   perPage: 6,
    // };

    /* The car's own search is checked for fit before it is paged
     * (P25009-492). The car comes from Redux, or — on a reload, before
     * useCurrentCar has put it back — straight from this browser's storage.
     * On the server neither exists, so the server render stays an ordinary
     * search and the browser's own loadData does the fit search. */
    const plateState = getState()?.currentLicensePlate || {};
    const fitCar = fitSearchCar({
      plateInfo: plateState.currentLicensePlate,
      plateReg: plateState.currentLicensePlateReg,
      storedCar: isBrowser ? readStoredCar() : null,
      regNumber,
      carId,
      passar,
    });

    /* P25009-512 BR-7 — a listing number goes straight to its listing(s).
     * Not in own-stock mode: that is the seller's own stock, already scoped. */
    const listingNumber = showInternalStock
      ? null
      : listingLookupCore.normaliseTireId(normalListingsParams.keywords);
    if (listingNumber) {
      const matched = await dispatch(searchByListingNumber(listingNumber, normalListingsParams, config));
      if (matched) return matched;
    }

    const searchListingsCall = searchListings(
      normalListingsParams,
      config,
      showOutOfStock,
      showInternalStock,
      false,
      fitCar,
      radiusSearch
    );

    /* The dealer adverts ("admin-adverts") used to be fetched here and merged
     * into the results every fourth card. P25009-486 BR-1 takes them out of
     * the result list, so they are no longer fetched at all. */
    return dispatch(searchListingsCall);
  };
};
